import { Mic, Circle } from 'lucide-react';
import { Spinner } from './ui';
import { cn } from '../lib/cn';
import type { RecorderState } from '../hooks/useVoiceRecorder';

interface VoiceMicButtonProps {
  state: RecorderState;
  remainingMs: number;
  onPressStart: () => void;
  onPressEnd: () => void;
  className?: string;
}

function formatRemaining(ms: number): string {
  const s = Math.ceil(ms / 1000);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${ss.toString().padStart(2, '0')}`;
}

export function VoiceMicButton({
  state,
  remainingMs,
  onPressStart,
  onPressEnd,
  className = '',
}: VoiceMicButtonProps) {
  if (state === 'unsupported') return null;

  if (state === 'pending') {
    return (
      <div
        className={cn(
          'flex items-center justify-center w-10 h-10 rounded-full bg-tg-section-bg',
          className,
        )}
        aria-label="Распознаём..."
      >
        <Spinner size="sm" />
      </div>
    );
  }

  if (state === 'recording') {
    return (
      <div
        role="button"
        aria-label="Идёт запись (отпустите, чтобы остановить)"
        onPointerUp={onPressEnd}
        onPointerLeave={onPressEnd}
        onPointerCancel={onPressEnd}
        onContextMenu={(e) => e.preventDefault()}
        style={{ touchAction: 'none', userSelect: 'none' }}
        className={cn(
          'flex items-center gap-1.5 h-10 px-3 rounded-full',
          'bg-destructive text-destructive-on text-xs font-medium animate-pulse select-none',
          className,
        )}
      >
        <Circle aria-hidden size={10} fill="currentColor" />
        <span className="tabular-nums">{formatRemaining(remainingMs)}</span>
      </div>
    );
  }

  return (
    <div
      role="button"
      aria-label="Удерживайте, чтобы записать голосом"
      onPointerDown={onPressStart}
      onPointerUp={onPressEnd}
      onPointerLeave={onPressEnd}
      onPointerCancel={onPressEnd}
      onContextMenu={(e) => e.preventDefault()}
      style={{ touchAction: 'none', userSelect: 'none' }}
      className={cn(
        'flex items-center justify-center w-10 h-10 rounded-full',
        'bg-tg-section-bg border border-tg-hint/40 text-tg-text',
        'transition-[transform,opacity] duration-150 ease-smooth',
        'active:scale-95 active:opacity-80 cursor-pointer select-none',
        className,
      )}
    >
      <Mic aria-hidden size={18} />
    </div>
  );
}
